import { useEffect, useState } from "preact/hooks";
import { api } from "../api";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const s = atob(base64url.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(base64url.length / 4) * 4, "="));
  return Uint8Array.from(s, (c) => c.charCodeAt(0));
}

// Notifications push (exigence C1) : activation sur cet appareil, puis réglage par adresse.
export function Notifications({ notify }: { notify: (text: string) => void }) {
  const [publicKey, setPublicKey] = useState<string | null>(null);
  const [addresses, setAddresses] = useState<{ address: string; notify: boolean; hidden: boolean }[]>([]);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.push().then((p) => { setPublicKey(p.publicKey); setAddresses(p.addresses); }).catch(() => {});
    if (supported()) navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then(setSubscription).catch(() => {});
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      if (await Notification.requestPermission() !== "granted") {
        notify("Notifications refusées : autorisez-les dans les réglages du navigateur pour ce site.");
        return;
      }
      const registration = await navigator.serviceWorker.ready;
      const sub = await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey!) });
      await api.subscribePush(sub.toJSON());
      setSubscription(sub);
      notify("Notifications activées sur cet appareil");
    } catch (e) {
      notify((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    if (!subscription) return;
    setBusy(true);
    await api.unsubscribePush(subscription.endpoint).catch(() => {});
    await subscription.unsubscribe().catch(() => {});
    setSubscription(null);
    setBusy(false);
    notify("Notifications désactivées sur cet appareil");
  };

  const test = async () => {
    const r = await api.testPush().catch((e) => { notify((e as Error).message); return null; });
    if (r) notify(r.sent ? "Notification d'essai envoyée" : "L'envoi de la notification a échoué");
  };

  return (
    <>
      <h2>Notifications</h2>
      {!supported() ? (
        <p class="sub">Ce navigateur ne gère pas les notifications. Sur iPhone, installez d'abord l'application sur l'écran d'accueil.</p>
      ) : !publicKey ? (
        <p class="sub">Les notifications ne sont pas encore configurées sur le serveur.</p>
      ) : subscription ? (
        <p class="rule-buttons">
          <button class="btn" onClick={test}>Envoyer une notification d'essai</button>
          <button class="btn danger" onClick={disable} disabled={busy}>Désactiver sur cet appareil</button>
        </p>
      ) : (
        <p><button class="btn primary" onClick={enable} disabled={busy}>Activer sur cet appareil</button></p>
      )}
      {addresses.length > 0 && (
        <>
          <p class="sub">Adresses qui préviennent à l'arrivée d'un e-mail (sauf s'il est rangé ou marqué comme lu par une règle) :</p>
          {addresses.map((a) => (
            <label class="check">
              <input type="checkbox" checked={a.notify && !a.hidden} disabled={a.hidden} onChange={async (e) => {
                const on = (e.target as HTMLInputElement).checked;
                setAddresses(addresses.map((x) => (x.address === a.address ? { ...x, notify: on } : x)));
                await api.setNotify(a.address, on).catch((err) => notify((err as Error).message));
              }} />
              {a.address}
              {a.hidden && <span class="sub">masquée dans le menu : jamais de notification</span>}
            </label>
          ))}
        </>
      )}
    </>
  );
}
